import {
  createRequester,
  RequestParams,
  SearchForFacetValuesResponse,
  SearchParams,
  SearchResponse,
} from '@algolia/autocomplete-preset-algolia';
import { Hit } from '@algolia/client-search';
import { SearchClient } from 'algoliasearch';

/**
 * This was copied directly from the official autocomplete repo
 * @link https://github.com/algolia/autocomplete/blob/7f5ba08fde5278cfbb944cfde5d78bda0f3855e1/packages/autocomplete-preset-algolia/src/constants/index.ts
 * @author Franklin <franklin.clark@thoughtspot.com>
 */
const HIGHLIGHT_PRE_TAG = '__aa-highlight__';
const HIGHLIGHT_POST_TAG = '__/aa-highlight__';

/**
 *
 * The goal of this workaround is to put api key in POST body instead of the
 * headers. The reason for this is that Algolia Client 4.19 will only pass the
 * api key in the headers or the query string but our security api keys that
 * are generated on the BE are too large for the query string or headers
 *
 * @inspiredby https://github.com/algolia/algoliasearch-client-javascript/issues/1035#issuecomment-592014999
 * @author Franklin <franklin.clark@thoughtspot.com>
 * @param client
 * @param apiKey
 */
export function hackMoveSearchClientApiKeyFromHeaderToRequestBody(client: SearchClient, apiKey: string): SearchClient {
  // remove key from header
  delete (client.transporter as any).headers['x-algolia-api-key'];
  const read = client.transporter.read;

  // rewrite the read method to pass our request options (including the api key)
  // through the body of the request
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  client.transporter['read'] = (request, requestOptions) => {
    return read(request, { apiKey, ...requestOptions });
  };

  return client;
}

/**
 * Retrieves Algolia results from multiple indices.
 *
 * This was copied directly from the official autocomplete repo
 *  - The reason this has been copied is that we needed to prevent this method from throwing an error
 *    in the event that the search client doesn't have the apikey and appid set in the header.  Our search client
 *    likely won't have the apikey assigned to the header because of an issue regarding the browser limits
 *    for header size {@see https://thoughtspot.atlassian.net/browse/UCM-559}
 * @link https://github.com/algolia/autocomplete/blob/7f5ba08fde5278cfbb944cfde5d78bda0f3855e1/packages/autocomplete-preset-algolia/src/requester/getAlgoliaResults.ts
 * @author Franklin <franklin.clark@thoughtspot.com>
 */
export function getAlgoliaResults<TTHit>(requestParams: RequestParams<TTHit> & { apiKey: string; appId: string }) {
  if (typeof requestParams.searchClient !== 'object') {
    throw new Error('The `searchClient` parameter is required for getAlgoliaResults({ searchClient }).');
  }

  /**
   * This was copied directly from the official autocomplete repo
   * @link https://github.com/algolia/autocomplete/blob/7f5ba08fde5278cfbb944cfde5d78bda0f3855e1/packages/autocomplete-preset-algolia/src/search/fetchAlgoliaResults.ts
   * @author Franklin <franklin.clark@thoughtspot.com>
   */
  function fetchAlgoliaResults<TRecord>({
    searchClient,
    queries,
    userAgents = [],
  }: SearchParams): Promise<Array<SearchResponse<TRecord> | SearchForFacetValuesResponse>> {
    if (typeof searchClient.addAlgoliaAgent === 'function') {
      // added 01.10.2023
      // We specify webapp as a user agent instead of autocomplete-js since we customized this method
      const coreUserAgents = [
        {
          segment: 'webapp-ui',
          version: '1.0',
        },
      ];

      const algoliaAgents: any[] = [...coreUserAgents, ...userAgents];

      algoliaAgents.forEach(({ segment, version }) => {
        searchClient.addAlgoliaAgent(segment, version);
      });
    }

    return searchClient
      .search<TRecord>(
        queries.map((searchParameters) => {
          const { params, ...headers } = searchParameters;

          return {
            ...headers,
            params: {
              hitsPerPage: 5,
              highlightPreTag: HIGHLIGHT_PRE_TAG,
              highlightPostTag: HIGHLIGHT_POST_TAG,
              ...params,
            },
          };
        })
      )
      .then((response) => {
        return response.results.map((result: any, resultIndex) => ({
          ...result,
          hits: result.hits?.map((hit: Hit<any>) => ({
            ...hit,
            // Bring support for the Insights plugin.
            __autocomplete_indexName: result.index || queries[resultIndex].indexName,
            __autocomplete_queryID: result.queryID,
            __autocomplete_algoliaCredentials: {
              appId: requestParams.appId,
              apiKey: requestParams.apiKey,
            },
          })),
        }));
      });
  }

  /**
   * This was copied directly from the official autocomplete repo
   * @link https://github.com/algolia/autocomplete/blob/7f5ba08fde5278cfbb944cfde5d78bda0f3855e1/packages/autocomplete-preset-algolia/src/requester/createAlgoliaRequester.ts
   * @author Franklin <franklin.clark@thoughtspot.com>
   */
  const createAlgoliaRequester = createRequester(fetchAlgoliaResults, 'algolia');

  const requester = createAlgoliaRequester({
    transformResponse: (response) => response.hits,
  });

  return requester(requestParams);
}
